import { BldConditionTypeEnum } from "common/enums/BldConditionTypeEnum";
import { EstateTypeNameEnum } from "common/enums/EstateTypeEnum";
import { GroupEnum } from "common/enums/GroupEnum";
import { MediationKindEnum } from "common/enums/MediationKindEnum";
import { SearchModeEnum } from "common/enums/SearchModeEnum";
import { StatusNameEnum } from "common/enums/StatusEnum";
import { listEstates } from "common/functions/api/estates";
import { parseEstateId } from "common/functions/estateId";
import { dateToTermString } from "common/functions/estates";
import { getDefaultCities } from "common/functions/jsonMasters/cities";
import {
  getDefaultPrefectures,
  getPrefectures
} from "common/functions/jsonMasters/prefectures";
import { City } from "common/interfaces/City";
import { Prefecture } from "common/interfaces/Prefecture";
import { MinimalEstate } from "common/queries/minimalEstates";
import { MainContextContainer } from "components/Main";
import { EstateLocationInputMode } from "components/Parts/Organisms/EstateLocationInputDialog/enums";
import { differenceInDays } from "date-fns";
import { Estate } from "graphql/API";
import { useCityHooks } from "hooks/cityHooks";
import { isEmpty } from "lodash";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { CheckEstatesCondition } from "../../../../common/interfaces/CheckEstatesCondition";
import { useSearchCounterMethods } from "../SearchPage/hooks/methods/counters";
import {
  estateTypeNumber,
  isDomain as isDomainChecker,
  statusNumber
} from "./functions";

const estateIdSortKey = (id: string) => {
  try {
    const { prefix, recNo } = parseEstateId(id);

    if (prefix === "R") {
      return { prefixTier: 0, prefix, recNo };
    }
    if (/^R[A-Z]+$/.test(prefix)) {
      return { prefixTier: 1, prefix, recNo };
    }

    return { prefixTier: 2, prefix, recNo };
  } catch {
    return { prefixTier: 3, prefix: id, recNo: 0 };
  }
};

const compareEstateIdForSort = (
  aId: string | null | undefined,
  bId: string | null | undefined
): number => {
  const a = estateIdSortKey(aId ?? "");
  const b = estateIdSortKey(bId ?? "");

  if (a.prefixTier !== b.prefixTier) {
    return a.prefixTier - b.prefixTier;
  }

  if (a.prefixTier === 1) {
    const prefixCmp = a.prefix.localeCompare(b.prefix);
    if (prefixCmp !== 0) {
      return prefixCmp;
    }
  }

  return a.recNo - b.recNo;
};

export const useCheckEstatesHooks = (ownDraft: boolean) => {
  const initSearchRef = useRef(true);

  const mainContext = useContext(MainContextContainer);

  const [prepared, setPrepared] = useState(false);
  const [condition, setCondition] = useState<CheckEstatesCondition>({
    mode: ownDraft ? SearchModeEnum.domain : SearchModeEnum.all,
    statuses: ownDraft
      ? [StatusNameEnum.位置不明]
      : [StatusNameEnum.公開, StatusNameEnum.位置不明],
    estateTypes: [
      EstateTypeNameEnum.土地,
      EstateTypeNameEnum.分譲地,
      EstateTypeNameEnum.新築建売,
      EstateTypeNameEnum.中古戸建,
      ...(ownDraft
        ? [EstateTypeNameEnum.マンション, EstateTypeNameEnum.事業用]
        : [])
    ],
    mediationKinds: [
      MediationKindEnum.一般,
      MediationKindEnum.専任,
      MediationKindEnum.売主,
      MediationKindEnum.仲介,
      MediationKindEnum.代理,
      MediationKindEnum.専属
    ],
    prefs: [],
    cities: [],
    area: null,
    company: mainContext.company
  });

  const [prefectures, setPrefectures] = useState<Prefecture[]>([]);
  const { cities } = useCityHooks(condition.prefs, mainContext.setFatal);

  const [openModeSelectDialog, setOpenModeSelectDialog] = useState(false);
  const [openStatusSelectDialog, setOpenStatusSelectDialog] = useState(false);
  const [openEstateTypeSelectDialog, setOpenEstateTypeSelectDialog] =
    useState(false);
  const [openMediationKindSelectDialog, setOpenMediationKindSelectDialog] =
    useState(false);
  const [openSwitchAreaDialog, setOpenSwitchAreaDialog] = useState(false);

  const [estates, setEstates] = useState<MinimalEstate[]>([]);

  const [clickedEstateForLocation, setClickedEstateForLocation] = useState<
    MinimalEstate | undefined
  >(undefined);
  const [inputModeForLocation, setInputModeForLocation] = useState<
    "update" | "delete" | undefined
  >(undefined);

  const [clickedEstateForReport, setClickedEstateForReport] = useState<
    MinimalEstate | undefined
  >(undefined);
  const [inputModeForReport, setInputModeForReport] = useState<
    "report" | undefined
  >(undefined);

  const { incrementSiteAtomicCounter } = useSearchCounterMethods(mainContext);

  const [busy, setBusy] = useState(false);

  const estatesCount = useMemo(() => estates.length, [estates]);
  const isAdministrator = useMemo(() => {
    return mainContext.groups?.includes(GroupEnum.Administrator) ?? false;
  }, [mainContext.groups]);

  const setDefaultPrefectures = useCallback(() => {
    const defaultPrefectures = getDefaultPrefectures(prefectures, [
      ...(mainContext.company?.areas ?? []),
      ...(mainContext.company?.areaPrefs ?? [])
    ]);

    if (defaultPrefectures) {
      setCondition((condition) => ({
        ...condition,
        prefs: defaultPrefectures
      }));
    }
  }, [mainContext.company?.areaPrefs, mainContext.company?.areas, prefectures]);

  const setDefaultCities = useCallback(() => {
    const defaultCities = getDefaultCities(cities, mainContext.company?.areas);

    if (defaultCities) {
      setCondition((condition) => ({
        ...condition,
        cities: defaultCities
      }));

      setPrepared(true);
    }
  }, [cities, mainContext.company?.areas]);

  const onModeClick = useCallback(() => {
    setOpenModeSelectDialog(true);
  }, []);

  const onModeSelectDialogClose = useCallback(() => {
    setOpenModeSelectDialog(false);
  }, []);

  const onModeSelectDialogOkClick = useCallback(
    (mode: SearchModeEnum.all | SearchModeEnum.domain) => {
      setCondition((condition) => ({
        ...condition,
        mode
      }));
      setOpenModeSelectDialog(false);
    },
    []
  );

  const onStatusesClick = useCallback(() => {
    setOpenStatusSelectDialog(true);
  }, []);

  const onStatusSelectDialogClose = useCallback(() => {
    setOpenStatusSelectDialog(false);
  }, []);

  const onStatusSelectDialogOkClick = useCallback(
    (statuses: StatusNameEnum[]) => {
      setCondition((condition) => ({ ...condition, statuses }));
      setOpenStatusSelectDialog(false);
    },
    []
  );

  const onEstateTypesClick = useCallback(() => {
    setOpenEstateTypeSelectDialog(true);
  }, []);

  const onEstateTypeSelectDialogClose = useCallback(() => {
    setOpenEstateTypeSelectDialog(false);
  }, []);

  const onEstateTypeSelectDialogOkClick = useCallback(
    (estateTypes: EstateTypeNameEnum[]) => {
      setCondition((condition) => ({ ...condition, estateTypes }));
      setOpenEstateTypeSelectDialog(false);
    },
    []
  );

  const onMediationKindsClick = useCallback(() => {
    setOpenMediationKindSelectDialog(true);
  }, []);

  const onMediationKindSelectDialogClose = useCallback(() => {
    setOpenMediationKindSelectDialog(false);
  }, []);

  const onMediationKindSelectDialogOkClick = useCallback(
    (mediationKinds: MediationKindEnum[]) => {
      setCondition((condition) => ({ ...condition, mediationKinds }));
      setOpenMediationKindSelectDialog(false);
    },
    []
  );

  const onCitiesClick = useCallback(() => {
    setOpenSwitchAreaDialog(true);
  }, []);

  const onSwitchAreaDialogClose = useCallback(() => {
    setOpenSwitchAreaDialog(false);
  }, []);

  const onSwitchAreaDialogOkClick = useCallback(
    (selectedPrefectures: Prefecture[], selectedCities: City[]) => {
      setCondition((condition) => ({
        ...condition,
        prefs: selectedPrefectures,
        cities: selectedCities
      }));
      setOpenSwitchAreaDialog(false);
    },
    []
  );

  const onAreaChange = useCallback((area: string | null | undefined) => {
    setCondition((condition) => ({ ...condition, area }));
  }, []);

  const onSearchClick = useCallback(async () => {
    try {
      setBusy(true);

      const validatedCondition: CheckEstatesCondition = {
        ...condition,
        statuses: condition.statuses.includes(StatusNameEnum.位置不明)
          ? [
              ...condition.statuses.filter(
                (x) => x !== StatusNameEnum.位置不明
              ),
              StatusNameEnum.下書き
            ]
          : condition.statuses,
        company: mainContext.company
      };

      const getEstates = await listEstates(
        validatedCondition,
        mainContext.groups?.includes(GroupEnum.Administrator) ?? false
      );

      const sanitizedEstates = getEstates.map((estate) => ({
        ...estate,
        fullAddress:
          estate.status === StatusNameEnum.成約済み &&
          isEmpty(estate.fullAddress)
            ? `${estate.prefecture}${estate.city}${estate.area}`
            : estate.fullAddress,
        bldConditionType:
          estate.estateType === EstateTypeNameEnum.土地 ||
          estate.estateType === EstateTypeNameEnum.分譲地
            ? estate.bldConditionType === BldConditionTypeEnum.建築条件あり
              ? "あり"
              : "なし"
            : "",
        companyName:
          estate.status === StatusNameEnum.成約済み &&
          isDomainChecker(estate, mainContext.company) === false &&
          isAdministrator === false
            ? "-"
            : [
                estate.companyName,
                estate.subCompanyName,
                estate.subCompany2Name
              ]
                .filter((x) => x)
                .join(" / ") || "-",
        unitPrice:
          estate.estateType === EstateTypeNameEnum.土地 ||
          estate.estateType === EstateTypeNameEnum.分譲地
            ? estate.unitPrice
            : undefined,
        updatedAt:
          estate.status === StatusNameEnum.成約済み
            ? dateToTermString(
                estate.notFoundAt ?? estate.updatedAt ?? "-",
                true
              )
            : "-",
        estatePrice:
          estate.status === StatusNameEnum.成約済み &&
          isDomainChecker(estate, mainContext.company) === false &&
          isAdministrator === false
            ? Math.round((estate.estatePrice || 0) / 10) * 10
            : estate.estatePrice,
        livingDays:
          estate.status === StatusNameEnum.成約済み
            ? estate.createdAt &&
              (estate.notFoundAt ?? estate.updatedAt) &&
              differenceInDays(
                (estate.notFoundAt ?? estate.updatedAt)!,
                estate.createdAt
              ) > 0
              ? differenceInDays(
                  (estate.notFoundAt ?? estate.updatedAt)!,
                  estate.createdAt
                )
              : undefined
            : estate.createdAt &&
                differenceInDays(new Date(), estate.createdAt) > 0
              ? differenceInDays(new Date(), estate.createdAt)
              : undefined
      }));

      setEstates(
        sanitizedEstates.sort(
          (a, b) =>
            estateTypeNumber(a) - estateTypeNumber(b) ||
            statusNumber(a) - statusNumber(b) ||
            compareEstateIdForSort(a.id, b.id)
        )
      );
    } finally {
      setBusy(false);
    }
  }, [condition, isAdministrator, mainContext.company, mainContext.groups]);

  const onEstateClick = useCallback(
    (estate: MinimalEstate, mode: "update" | "delete" | "report") => {
      if (mode === "report") {
        setClickedEstateForReport(estate);
        setInputModeForReport(mode);
      } else {
        setClickedEstateForLocation(estate);
        setInputModeForLocation(mode);
      }
    },
    [setClickedEstateForLocation, setInputModeForLocation]
  );

  const onEstatesChange = useCallback(
    (
      changedEstates: {
        estate: Estate;
        mode: "create" | "update" | "delete" | null;
      }[]
    ) => {
      changedEstates.forEach((changedEstate) => {
        const currentEstates = [...estates];
        const operatedEstateIndex = currentEstates.findIndex(
          (x) => x.id === changedEstate.estate.id
        );
        switch (changedEstate.mode) {
          case "delete":
            setEstates((estates) =>
              estates.filter((x) => x.id !== changedEstate.estate.id).slice()
            );
            break;
          case "update":
            setEstates((estates) => {
              const newEstates = estates.slice();

              newEstates[operatedEstateIndex] = {
                ...newEstates[operatedEstateIndex],
                ...changedEstate.estate,
                clickedCount: 0,
                blocks: []
              };

              return newEstates;
            });
            break;
        }
      });
    },
    [estates]
  );

  const onLocationInputDialogOkButtonClick = useCallback(
    (mode: EstateLocationInputMode, inputValues: Estate | null) => {
      if (inputValues) {
        onEstatesChange([
          {
            estate: inputValues,
            mode:
              mode === EstateLocationInputMode.Update
                ? "update"
                : mode === EstateLocationInputMode.Delete
                  ? "delete"
                  : null
          }
        ]);
      }
    },
    [onEstatesChange]
  );

  const onReportInputDialogOkButtonClick = useCallback(() => {}, []);

  const onInputClose = useCallback(() => {
    setClickedEstateForLocation(undefined);
    setInputModeForLocation(undefined);
    setClickedEstateForReport(undefined);
    setInputModeForReport(undefined);
  }, []);

  useEffect(() => {
    getPrefectures(false).then((prefectures) => {
      if (prefectures) {
        setPrefectures(prefectures);
      }
    });
  }, []);

  useEffect(() => {
    if (!prepared && mainContext.company && prefectures.length > 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setDefaultPrefectures();
    }
  }, [mainContext.company, prefectures, prepared, setDefaultPrefectures]);

  useEffect(() => {
    if (!prepared && mainContext.company && cities.length > 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setDefaultCities();
    }
  }, [cities, mainContext.company, prepared, setDefaultCities]);

  useEffect(() => {
    if (initSearchRef.current && prepared) {
      onSearchClick();
      initSearchRef.current = false;
    }
  }, [onSearchClick, prepared]);

  return {
    mainContext,

    condition,
    openModeSelectDialog,
    openStatusSelectDialog,
    openEstateTypeSelectDialog,
    openMediationKindSelectDialog,
    openSwitchAreaDialog,

    estates,
    estatesCount,
    isAdministrator,

    clickedEstateForLocation,
    inputModeForLocation,

    clickedEstateForReport,
    inputModeForReport,

    incrementSiteAtomicCounter,

    busy,

    onModeClick,
    onModeSelectDialogClose,
    onModeSelectDialogOkClick,

    onStatusesClick,
    onStatusSelectDialogClose,
    onStatusSelectDialogOkClick,

    onEstateTypesClick,
    onEstateTypeSelectDialogClose,
    onEstateTypeSelectDialogOkClick,

    onMediationKindsClick,
    onMediationKindSelectDialogClose,
    onMediationKindSelectDialogOkClick,

    onCitiesClick,
    onSwitchAreaDialogClose,
    onSwitchAreaDialogOkClick,

    onAreaChange,

    onSearchClick,

    onEstateClick,
    onLocationInputDialogOkButtonClick,
    onReportInputDialogOkButtonClick,
    onInputClose
  } as const;
};
